import React, { useEffect, useState, useRef } from 'react';
import { getDocs, query, orderBy } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../firebase.config.js';
import { useAgency } from '../AgencyContext';
import { AutoAwesome } from '@mui/icons-material';
import './PromptAutomatorMaster.css';

import PromptAutomatorLeftPanel from './PromptAutomatorLeftPanel';
import PromptAutomatorCenterPanel from './PromptAutomatorCenterPanel';
import PromptAutomatorRightPanel from './PromptAutomatorRightPanel';
import PromptAutomatorGeneratePanel from './PromptAutomatorGeneratePanel';
import PromptAutomatorHeader from './PromptAutomatorHeader';

function PromptAutomatorMaster({ selectedModel }) {
  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedPromptType, setSelectedPromptType] = useState(null);
  const [promptValues, setPromptValues] = useState({});
  const [currentPromptValue, setCurrentPromptValue] = useState('');
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState('');
  const [generatedPrompt, setGeneratedPrompt] = useState('');
  const [editingPromptId, setEditingPromptId] = useState(null);
  const [editingPromptValue, setEditingPromptValue] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [promptToDelete, setPromptToDelete] = useState(null);
  const [aiModels, setAiModels] = useState([]);
  const [user] = useAuthState(auth);
  const { getAgencyCollection, getAgencyDoc } = useAgency();
  const editTaskFromHeaderRef = useRef();

  // Load data when selectedModel changes
  useEffect(() => {
    setSelectedTask(null);
    setSelectedPromptType(null);
    setCurrentPromptValue('');
    setGeneratedPrompt('');
    
    const fetchData = async () => {
      if (!selectedModel) {
        setLoadingTasks(false);
        return;
      }
      
      try {
        setLoadingTasks(true);
        
        const aitaskCollection = getAgencyCollection('aitask');
        const aimodelCollection = getAgencyCollection('aimodel');
        
        if (!aitaskCollection || !aimodelCollection) {
          setLoadingTasks(false);
          return;
        }

        // Fetch tasks
        const taskSnapshot = await getDocs(aitaskCollection);
        const tasksData = taskSnapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data() 
        }));
        const activeTasks = tasksData.filter(task => task.status === 'active' && task.aiModelId === selectedModel);
        
        // Sort by order field, and assign order to tasks that don't have it
        const sortedTasks = activeTasks.sort((a, b) => {
          const orderA = a.order !== undefined ? a.order : 999999;
          const orderB = b.order !== undefined ? b.order : 999999;
          return orderA - orderB;
        });
        
        setTasks(sortedTasks);
        
        // Fetch AI models
        const modelSnapshot = await getDocs(aimodelCollection);
        const modelsData = modelSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setAiModels(modelsData.filter(model => model.status === 'active'));
        
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoadingTasks(false);
      }
    };
    
    fetchData();
  }, [selectedModel, getAgencyCollection]);

  // Auto-select first task when tasks are loaded
  useEffect(() => {
    if (tasks.length > 0 && !selectedTask) {
      const firstTask = tasks[0];
      setSelectedTask(firstTask);
      setSelectedPromptType({ id: 'promptTemplate', label: 'Prompt', icon: 'Settings' });
    }
  }, [tasks, selectedTask]);

  useEffect(() => {
    if (selectedTask) {
      setPromptValues(selectedTask.prompts || {});
    } else {
      setPromptValues({});
    }
  }, [selectedTask]);

  // Update selectedTask when tasks change (after edit)
  useEffect(() => {
    if (selectedTask && tasks.length > 0) {
      const updatedTask = tasks.find(task => task.id === selectedTask.id);
      if (updatedTask && JSON.stringify(updatedTask) !== JSON.stringify(selectedTask)) {
        setSelectedTask(updatedTask);
      }
    }
  }, [tasks, selectedTask]);

  // Handle task refresh after operations
  const refreshTasks = async () => {
    try {
      const aitaskCollection = getAgencyCollection('aitask');
      if (!aitaskCollection) return;
      
      const taskQuery = query(aitaskCollection, orderBy('order', 'asc'));
      const taskSnapshot = await getDocs(taskQuery);
      const tasksData = taskSnapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      }));
      const activeTasks = tasksData.filter(task => task.status === 'active' && task.aiModelId === selectedModel);
      setTasks(activeTasks);
    } catch (error) {
      console.error('Error refreshing tasks:', error);
    }
  };

  // Add direct tasks setter for instant UI updates
  refreshTasks.setTasksDirectly = setTasks;

  // Handle edit task from header
  const handleEditTaskFromHeader = (task) => {
    if (editTaskFromHeaderRef.current) {
      editTaskFromHeaderRef.current(task);
    }
  };

  // Function to select random element from array with weighting
  const getRandomFromArray = (arr) => {
    if (!arr || arr.length === 0) return '';
    
    const weightedArray = [];
    
    arr.forEach((item) => {
      const value = item?.value || item;
      const weight = item?.weight || 1;
      
      for (let i = 0; i < weight; i++) {
        weightedArray.push(value);
      }
    });
    
    const randomIndex = Math.floor(Math.random() * weightedArray.length);
    return weightedArray[randomIndex];
  };

  const generateRandomPrompt = async () => {
    if (!selectedTask || !promptValues) return;
    
    try {
      if (promptValues.promptTemplate && typeof promptValues.promptTemplate === 'string') {
        const randomValues = {
          outfit: getRandomFromArray(promptValues.outfit),
          shoes: getRandomFromArray(promptValues.shoes),
          location: getRandomFromArray(promptValues.location),
          pose: getRandomFromArray(promptValues.pose),
          photography: getRandomFromArray(promptValues.photography),
          quantity: getRandomFromArray(promptValues.quantity),
          light: getRandomFromArray(promptValues.light),
          eyes: getRandomFromArray(promptValues.eyes),
          age: getRandomFromArray(promptValues.age),
          skin: getRandomFromArray(promptValues.skin),
          hairs: getRandomFromArray(promptValues.hairs),
          mood: getRandomFromArray(promptValues.mood)
        };

        // Generate personality from personalityTemplate if it exists
        let personalityValue = '';
        if (promptValues.personalityTemplate && typeof promptValues.personalityTemplate === 'string') {
          personalityValue = promptValues.personalityTemplate;
          Object.entries(randomValues).forEach(([key, value]) => {
            if (['eyes', 'age', 'skin', 'hairs', 'mood'].includes(key) && value) {
              const placeholder = `{${key}}`;
              personalityValue = personalityValue.replace(new RegExp(placeholder, 'g'), value);
            }
          });
        }
        
        randomValues.personality = personalityValue;

        // Replace placeholders in the template
        let templatePrompt = promptValues.promptTemplate;
        
        Object.entries(randomValues).forEach(([key, value]) => {
          if (value) {
            const placeholder = `{${key}}`;
            templatePrompt = templatePrompt.replace(new RegExp(placeholder, 'g'), value);
          }
        });

        const generatedPrompt = templatePrompt.trim();
        setGeneratedPrompt(generatedPrompt);
        await navigator.clipboard.writeText(generatedPrompt);
        setNotification('Prompt generated and copied!');
      } else {
        setNotification('No template configured');
      }
    } catch (error) {
      console.error('Failed to generate or copy prompt:', error);
      setNotification('Failed to generate prompt');
    } finally {
      setTimeout(() => setNotification(''), 3000);
    }
  };

  if (!selectedModel) {
    return (
      <div className="prompt-automator-master">
        <div className="no-profile">
          <h2 className="no-profile__title">Select an AI Profile</h2>
          <p className="no-profile__message">Choose a profile from the menu at the bottom right to start managing your prompts.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="prompt-automator-master">
      <PromptAutomatorLeftPanel 
        tasks={tasks}
        selectedTask={selectedTask}
        setSelectedTask={setSelectedTask}
        setSelectedPromptType={setSelectedPromptType}
        setCurrentPromptValue={setCurrentPromptValue}
        setGeneratedPrompt={setGeneratedPrompt}
        loadingTasks={loadingTasks}
        selectedModel={selectedModel}
        aiModels={aiModels}
        refreshTasks={refreshTasks}
        setNotification={setNotification}
        onEditTaskFromHeader={editTaskFromHeaderRef}
      />
      
      <div className="right-content">
        {/* Header Panel */}
        <PromptAutomatorHeader 
          selectedTask={selectedTask} 
          onEditTask={handleEditTaskFromHeader}
          onGenerate={generateRandomPrompt}
          canGenerate={selectedTask && promptValues.promptTemplate}
          loadingTasks={loadingTasks}
        />
        
        {/* Generate Panel */}
        <div className="generate-panel-container">
          <PromptAutomatorGeneratePanel 
            generatedPrompt={generatedPrompt}
            setNotification={setNotification}
          />
        </div>
        
        {/* Bottom Panels */}
        <div className="bottom-panels">
          <PromptAutomatorCenterPanel 
            selectedTask={selectedTask}
            selectedPromptType={selectedPromptType}
            setSelectedPromptType={setSelectedPromptType}
            promptValues={promptValues}
            setCurrentPromptValue={setCurrentPromptValue}
          />
          
          <PromptAutomatorRightPanel 
            selectedTask={selectedTask}
            selectedPromptType={selectedPromptType}
            promptValues={promptValues}
            setPromptValues={setPromptValues}
            currentPromptValue={currentPromptValue}
            setCurrentPromptValue={setCurrentPromptValue}
            saving={saving}
            setSaving={setSaving}
            generatedPrompt={generatedPrompt}
            setGeneratedPrompt={setGeneratedPrompt}
            editingPromptId={editingPromptId}
            setEditingPromptId={setEditingPromptId}
            editingPromptValue={editingPromptValue}
            setEditingPromptValue={setEditingPromptValue}
            deleteModalOpen={deleteModalOpen}
            setDeleteModalOpen={setDeleteModalOpen}
            promptToDelete={promptToDelete}
            setPromptToDelete={setPromptToDelete}
            setTasks={setTasks}
            setSelectedTask={setSelectedTask}
            setNotification={setNotification}
            user={user}
            getAgencyDoc={getAgencyDoc}
          />
        </div>
      </div>



      {/* Toast Notification */}
      {notification && (
        <div className="prompt-toast">
          <AutoAwesome />
          <span>{notification}</span>
        </div>
      )}
    </div>
  );
}

export default PromptAutomatorMaster;